import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBurst } from '@fortawesome/free-solid-svg-icons';

interface Variable {
  name: string;
  description: string;
}

interface VariableAutocompleteProps {
  show: boolean;
  position: { top: number; left: number };
  searchTerm: string;
  onSelect: (variable: string) => void;
  onHide: () => void;
}

// Variables disponibles para greetings
const AVAILABLE_VARIABLES: Variable[] = [
  {
    name: 'salutationDayTime',
    description: 'Greeting based on time of day'
  },
  {
    name: 'agentName',
    description: 'Name of the conversational agent'
  },
  {
    name: 'customerName',
    description: 'Name of the customer'
  },
  {
    name: 'companyName',
    description: 'Name of the company'
  },
  {
    name: 'customerIsCompany',
    description: 'Returns "the company" in agent language'
  }
];

const VariableAutocomplete: React.FC<VariableAutocompleteProps> = ({
  show,
  position,
  searchTerm,
  onSelect,
  onHide
}) => {
  const [filteredVariables, setFilteredVariables] = useState<Variable[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm) {
      const filtered = AVAILABLE_VARIABLES.filter(variable =>
        variable.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVariables(filtered);
    } else {
      setFilteredVariables(AVAILABLE_VARIABLES);
    }
  }, [searchTerm]);

  useEffect(() => {
    // Cerrar al hacer clic fuera
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onHide]);

  if (!show) return null;

  return (
    <div
      ref={dropdownRef}
      className="position-absolute"
      style={{
        top: position.top,
        left: position.left,
        zIndex: 1050,
        minWidth: '300px',
        maxWidth: '400px'
      }}
    >
      <Dropdown.Menu show className="shadow-lg pt-0">
        <Dropdown.Header className="d-flex align-items-center fw-bold p-3 pb-0">
          <FontAwesomeIcon icon={faBurst} className="me-2 fs-8" />
          Available Variables
        </Dropdown.Header>
        <Dropdown.Divider />
        {filteredVariables.length === 0 ? (
          <Dropdown.ItemText className="text-muted">
            No variables found
          </Dropdown.ItemText>
        ) : (
          filteredVariables.map(variable => (
            <Dropdown.Item
              key={variable.name}
              onClick={() => onSelect(variable.name)}
              className="py-2"
            >
              <div>
                <strong className="text-primary">{`{{${variable.name}}}`}</strong>
                <div className="small text-muted">{variable.description}</div>
              </div>
            </Dropdown.Item>
          ))
        )}
      </Dropdown.Menu>
    </div>
  );
};

export default VariableAutocomplete;
